import { forwardRef, type AnchorHTMLAttributes, type ReactNode } from 'react';

import { linkClasses } from './linkClasses';

/**
 * docs/02-DESIGN-SYSTEM.md §4.1 — "Inline, navy, underline on hover, ↗
 * affordance for external".
 *
 * External links get `rel="noopener noreferrer"` and an icon that is announced
 * rather than silent: §7.4 requires that meaning never rests on a glyph alone,
 * so the affordance carries visually-hidden text. The caller supplies that
 * text, because it is user-visible and must come from the i18n bundle
 * (CLAUDE.md rule 2).
 *
 * This component styles a plain anchor. Internal navigation in the SPAs wraps
 * it in a router link that builds its href with `localizePath` — no component
 * ever writes a path literal (CLAUDE.md rule 9).
 */
export interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  /** Opens in a new tab, adds rel and shows the ↗ affordance. */
  external?: boolean;
  /**
   * Visually-hidden text appended for screen readers on an external link,
   * e.g. "(opens in a new tab)". Required when `external` is set so the
   * behaviour is not a surprise.
   */
  externalLabel?: string;
  /** Removes the inline underline; for links inside cards and list rows. */
  subtle?: boolean;
  children?: ReactNode;
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { external = false, externalLabel, subtle = false, className, children, ...props },
  ref,
) {
  return (
    <a
      ref={ref}
      className={linkClasses({ subtle }, className)}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      {...props}
    >
      {children}
      {external ? (
        <>
          <span aria-hidden="true" className="ms-0.5 inline-block">
            {'↗'}
          </span>
          {externalLabel ? <span className="sr-only"> {externalLabel}</span> : null}
        </>
      ) : null}
    </a>
  );
});
